import React, { useContext, useEffect, useState } from "react";
import { ChevronDownIcon, ChevronRightIcon } from "@heroicons/react/solid";
import icons from "../icons";
import classNames from "classnames";
import ConfigContext from "../contexts/AppContext";
import { createBrowserHistory } from "history";
import styled, { ThemeContext } from "styled-components";
import AppContext from "../contexts/AppContext";
import LeftBarContext from "../contexts/LeftBarContext";
import WithLoader from "./WithLoader";
import appFetch from "../appFetch";
import apiEndpoints from "../apiEndpoints";
import { appendQueryToUri } from "../util/generalUtil";

const Badge = styled.div`
  width: 18px;
  height: 18px;
  background: ${({ theme, active }) =>
    active ? theme?.colors.primary : theme?.colors.accent};
  font-size: 0.7em;
  color: ${({ theme, active }) => (active ? "#ffffff" : theme?.colors.primary)};
`;

const MenuText = styled.div`
  color: ${({ theme, active }) => (active ? theme.colors?.primary : "#838383")};
  font-weight: ${({ active }) => (active ? "600" : "normal")};
`;

function MenuList1({ options, name, setLoading, onClick }) {
  const { config, setConfig } = useContext(ConfigContext);
  const theme = useContext(ThemeContext);
  const { context, setContext } = useContext(AppContext);
  const { menuCollapse, setMobileMenuOpen } = useContext(LeftBarContext);
  const history = createBrowserHistory();
  const {
    location: { pathname },
  } = history;

  // normalize current path
  let _pathname = pathname.split("/").slice(-1)[0]?.toLowerCase() || "home";
  _pathname = decodeURI(_pathname.replace(/\//, "")).replace(/\-/g, " ");
  let patt = RegExp(_pathname, "i");

  // ---------- helper: find active menu recursively by name regex ----------
  const findMenuByRegex = (items, regex) => {
    if (!items) return null;
    for (const it of items) {
      if (it?.name && regex.test(it.name)) return it;
      if (it.options) {
        const found = findMenuByRegex(it.options, regex);
        if (found) return found;
      }
    }
    return null;
  };

  // ---------- helper: find menu by targetid recursively ----------
  const findMenuByTargetId = (items, targetId) => {
    if (!items || targetId === undefined || targetId === null) return null;
    const tIdStr = String(targetId);
    const _search = (node) => {
      if (!node) return null;
      if (node.targetid !== undefined && String(node.targetid) === tIdStr)
        return node;
      if (node.options && node.options.length) {
        for (const c of node.options) {
          const found = _search(c);
          if (found) return found;
        }
      }
      return null;
    };

    if (Array.isArray(items)) {
      for (const i of items) {
        const found = _search(i);
        if (found) return found;
      }
      return null;
    } else {
      return _search(items);
    }
  };


  let activeMenuById = findMenuByTargetId(options, context?.menuId);
  let activeMenuByPath = findMenuByRegex(options, patt);
  let activeMenu = activeMenuById || activeMenuByPath || null;
  let { targetid: menuId } = activeMenu || {};

  useEffect(() => {
    // keep context.menuId in sync when pathname changes (fallback behavior)
    // but main active detection prefers context.menuId (so navigating between same names works)
    const resolvedByPath = findMenuByRegex(options, patt);
    if (resolvedByPath && resolvedByPath.targetid !== context?.menuId) {
      setContext((prev) => ({ ...prev, menuId: resolvedByPath?.targetid }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);


  const containsTargetId = (nodeOrArray, targetId) => {
    if (!targetId || !nodeOrArray) return false;
    const tIdStr = String(targetId);
    const _check = (node) => {
      if (!node) return false;
      if (node.targetid !== undefined && String(node.targetid) === tIdStr)
        return true;
      if (node.options && node.options.length) {
        for (const child of node.options) {
          if (_check(child)) return true;
        }
      }
      return false;
    };

    if (Array.isArray(nodeOrArray)) {
      for (const n of nodeOrArray) {
        if (_check(n)) return true;
      }
      return false;
    } else {
      return _check(nodeOrArray);
    }
  };

  let groupedOrder = {};
  let optionsGroup = [];
  let optionsGroupLen = -1;

  options.forEach((o) => {
    if (o.group) {
      const groupKey = o.group.trim();
      if (!(groupKey in groupedOrder)) {
        groupedOrder[groupKey] = ++optionsGroupLen;
      }
      const index = groupedOrder[groupKey];
      if (!optionsGroup[index]) {
        optionsGroup[index] = {
          name: groupKey,
          icon: o.groupicon,
          options: [o],
        };
      } else {
        optionsGroup[index].options.push(o);
      }
    } else {
      optionsGroup[++optionsGroupLen] = o;
    }
  });

  const loadPage = (item) => {
    setLoading(true);
    let postData = {
      urlMenu: null,
      layoutId: null,
      targetId: item.targetid,
      target: "MENU",
      schemaId: null,
      txnGroup: null,
      parentDropValue: null,
    };

    appFetch
      .get(appendQueryToUri(apiEndpoints.menuView, postData))
      .then((res) => {
        let link = `${item.name.toLowerCase()}`;
        history.push(link.replace(/\s/g, "-"));
        let page = { ...context.pageConfig };
        let [leftArea, MainArea, rightArea] = page.subcomponents;
        page.subcomponents = [leftArea, res, rightArea];

        setContext((prev) => ({
          ...prev,
          menuId: item.targetid,
          pageConfig: page,
        }));
      })
      .catch((e) => console.log(e.message))
      .finally(() => setLoading(false));
  };

  const openNewPage = (item) => {
    setMobileMenuOpen(false);
    // compare by targetid (unique) so same-name items don't block navigation
    if (String(item.targetid) === String(activeMenu?.targetid)) return;
    loadPage(item);
  };

const iconMap = {
  home: "HomeIcon",
  setup: "SetupIcon",
  transaction: "TransactionIcon",
  approval: "ApprovalIcon",
  memo: "MemoIcon",
  open: "OpenIcon",
  dashboard: "DashboardIcon",
  confirmation: "ConfirmationIcon",
  payrollReports: "PayrollReportsIcon",
  otherReports: "OtherReportsIcon",
  essReports: "EssReportsIcon",
  bioData: "BioDataIcon",
  Online: "OnlineIcon",
  Users: "UsersIcon",
  Retail: "RetailIcon",
  Wallet: "WalletIcon",
  Ussd: "UssdIcon",
  Draws: "DrawsIcon",
  ThirdParty: "ThirdPartyIcon",
  Accuracy: "AccuracyIcon",
  Feeds: "FeedsIcon",
  Whatsapp: "WhatsappIcon",
  Error: "ErrorIcon",
  Calender: "CalenderIcon",
  map: "MapIcon",
  Statistics: "StatisticsIcon",
  OtherReports: "OtherReportsIcon",
  Ticket: "TicketIcon",
  Search: "SearchIcon",
  report: "OtherReportsIcon",
  Forms: "FormsIcon",
  module: "FormsIcon",
  reports: "StatisticsIcon",
  money: "MoneyIcon",
  action: "ActionIcon",
  followup: "FollowUpIcon",
  pending: "PendingIcon",
  qualified: "PassIcon",
  schedule: "ScheduledIcon",
  demo: "DemoIcon",
  proposal: "PlanIcon",
  onboarding: "OnboardIcon",
  crm: "CRMIcon",
  sales: "SalesIcon",
  inventory: "InventoryIcon",
  horizontalbarchart: "HorizontalBarIcon",
  verticalbarchart: "VerticalBarIcon",
  scatterchart: "ScatterIcon",
  layout: "LayoutIcon",
  piechart: "PieIcon",
  linechart: "LineIcon",
  bell: "BellIcon",
  User: "UserIcon",
  Browser: "BrowserIcon",
  All: "AllIcon",
  Pos: "PosIcon",
  Football: "FootballIcon",
  Bingo: "BingoIcon",
  Lottery: "LotteryIcon",
  "": "setup",
};

  const Tree = ({ item, current, isExpanded, toggleGroup }) => {
    const [activeItem, setActiveItem] = useState();

    const showSubOptions = (item) => {
      if (item.options) return toggleGroup();
      return openNewPage(item);
    };

    const { name, icon, badge, options } = item;
    const menuIcon = iconMap[icon] || "setup";

    // mark group header active if any child contains activeMenu.targetid OR the group itself matches
    const active =
      (activeMenu?.targetid !== undefined &&
        containsTargetId(item.options, activeMenu?.targetid)) ||
      (item.targetid !== undefined &&
        activeMenu?.targetid !== undefined &&
        String(item.targetid) === String(activeMenu.targetid));

    return (
      <div>
        <li
          className="mb-3 flex items-center"
          onClick={() => showSubOptions(item)}
        >
          {icons[menuIcon] &&
            React.createElement(icons[menuIcon], {
              width: 20,
              height: null,
              fill: active ? theme?.colors?.primary : "#AAAAAA",
              stroke: active ? theme?.colors?.primary : "#AAAAAA",
            })}
          <MenuText
            className={classNames(
              "capitalize leading-1 text-gray-custom tracking-wide flex items-center",
              { "pl-3": !!icons[menuIcon] }
            )}
            active={active}
          >
            <span className="pr-2">{item.name}</span>
            {typeof badge !== "undefined" && (
              <Badge className="font-semibold ml-2 rounded-sm inline-grid place-items-center rounded-xl">
                {badge}
              </Badge>
            )}
            {item.options && (
              <span className="rounded-full shadow">
                {isExpanded ? (
                  <ChevronDownIcon width={18} height={18} />
                ) : (
                  <ChevronRightIcon width={18} height={18} />
                )}
              </span>
            )}
          </MenuText>
        </li>
        <div>
          {item.options && isExpanded && (
            <TreeSub item={item.options} setActiveItem={setActiveItem} />
          )}
        </div>
      </div>
    );
  };

  const TreeSub = ({ item, setActiveItem }) => {
    return (
      <ul>
        {item.map((option, idx) => {
          let { name, icon, badge } = option;
          let menuIcon = iconMap[icon];
          // active if this option's targetid matches activeMenu OR any descendant contains it
          const active =
            (activeMenu?.targetid !== undefined &&
              option.targetid !== undefined &&
              String(option.targetid) === String(activeMenu.targetid)) ||
            containsTargetId(option.options, activeMenu?.targetid);

          setActiveItem(activeMenu);

          return (
            <div key={idx}>
              <li
                className="mb-2 ml-6 flex items-center cursor-pointer"
                onClick={() =>
                  option.options ? null /* expand handled elsewhere */ : openNewPage(option)
                }
              >
                {icons[menuIcon] &&
                  React.createElement(icons[menuIcon], {
                    width: 20,
                    height: null,
                    fill: active ? theme?.colors?.primary : "#AAAAAA",
                    stroke: active ? theme?.colors?.primary : "#AAAAAA",
                  })}
                <MenuText
                  className="flex capitalize pl-3 leading-1 text-gray-custom tracking-wide flex items-center"
                  active={active}
                >
                  <span>{name}</span>
                  {typeof badge != "undefined" && (
                    <Badge className="font-semibold ml-2 rounded-sm inline-grid place-items-center rounded-xl">
                      {badge}
                    </Badge>
                  )}
                </MenuText>
              </li>

              {/* render children if present */}
              {option.options && (
                <div className="ml-2">
                  <TreeSubSub item={option.options} />
                </div>
              )}
            </div>
          );
        })}
      </ul>
    );
  };

  const TreeSubSub = ({ item }) => {
    return (
      <ul>
        {item.map((option, idx) => {
          const { name, icon, badge } = option;
          const menuIcon = iconMap[icon];
          // leaf active — simple direct targetid compare
          const active =
            activeMenu?.targetid !== undefined &&
            option.targetid !== undefined &&
            String(option.targetid) === String(activeMenu.targetid);

          return (
            <li
              key={idx}
              className="mb-2 ml-10 flex items-center cursor-pointer"
              onClick={() => openNewPage(option)}
            >
              {icons[menuIcon] &&
                React.createElement(icons[menuIcon], {
                  width: 18,
                  height: null,
                  fill: active ? theme?.colors?.primary : "#AAAAAA",
                  stroke: active ? theme?.colors?.primary : "#AAAAAA",
                })}
              <MenuText
                className="flex capitalize pl-3 leading-1 text-gray-custom tracking-wide flex items-center"
                active={active}
              >
                <span>{name}</span>
                {typeof badge != "undefined" && (
                  <Badge className="font-semibold ml-2 rounded-sm inline-grid place-items-center rounded-xl">
                    {badge}
                  </Badge>
                )}
              </MenuText>
            </li>
          );
        })}
      </ul>
    );
  };

  const [showGroup, setShowGroup] = useState(true);
  const [expandedGroups, setExpandedGroups] = useState({});

  const toggleGroup = (index) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const showSubOptions = (item) => {
    if (item.options) return setShowGroup(!showGroup);
    return openNewPage(item);
  };

  return (
    <div className="py-2 z-50">
      {!menuCollapse && (
        <>
          <header className="flex items-center mb-6 border-y border-gray-200 bg-gray-100 -mx-8 ">
            <h3 className="font-semibold tracking-wide ml-8 py-1">{name}</h3>
          </header>
          <ul>
            {optionsGroup?.map((item, idx) => (
              <li key={idx} className="mb-3 items-center cursor-pointer">
                <Tree
                  item={item}
                  current={idx}
                  isExpanded={expandedGroups[idx]}
                  toggleGroup={() => toggleGroup(idx)}
                />
              </li>
            ))}
          </ul>
        </>
      )}

      {menuCollapse && (
        <>
          <ul>
            {optionsGroup?.map((item, idx) => {
              let { name, icon, badge } = item;
              let menuIcon = iconMap[icon];
              const active = new RegExp(name, "i").test(activeMenu?.name);
              return (
                <li
                  key={idx}
                  className="group mb-6 flex items-center cursor-pointer"
                  onClick={() => showSubOptions(item)}
                >
                  <div className="relative">
                    {icons[menuIcon] &&
                      React.createElement(icons[menuIcon], {
                        width: 20,
                        height: null,
                        fill: active ? theme?.colors?.primary : "#AAAAAA",
                        stroke: active ? theme?.colors?.primary : "#AAAAAA",
                      })}
                    {typeof badge != "undefined" && (
                      <Badge
                        active={active}
                        className="absolute top-0 -translate-y-3 font-semibold ml-2 rounded-sm inline-grid place-items-center rounded-xl"
                      >
                        {badge}
                      </Badge>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </>
      )}
    </div>
  );
}

export default WithLoader(MenuList1);
